import { resumeAudio, suspendAudio } from "./audio";

const GAME_KEYS = new Set([
  "KeyA",
  "KeyD",
  "ArrowLeft",
  "ArrowRight",
  "ArrowUp",
  "Space",
  "Enter",
  "KeyR",
  "KeyW",
]);

const TUCK_KEYS = ["Space", "KeyW", "ArrowUp"];

const held = new Set<string>();
let injected: Set<string> | null = null;
let steerOverride: number | null = null;
let tuckOverride: boolean | null = null;
let touchX = 0;
let touchTuck = false;
let pointerDown = false;
let canvasPointerId: number | null = null;

function isEditable(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target.isContentEditable
  );
}

function activeKeys(): Set<string> {
  return injected ?? held;
}

export function attachInput(canvas: HTMLCanvasElement): () => void {
  const onDown = (e: KeyboardEvent) => {
    // A text field owns its keystrokes -- the initials box on the results
    // card, in particular. Swallowing Space or Enter there would stop the
    // player typing or posting.
    if (isEditable(e.target)) return;
    // Any input is a chance to recover a context Safari parked as
    // "interrupted" behind our back (a call, Siri, Control Center).
    resumeAudio();
    held.add(e.code);
    if (GAME_KEYS.has(e.code)) e.preventDefault();
  };
  const onUp = (e: KeyboardEvent) => {
    held.delete(e.code);
  };
  const clear = () => held.clear();
  const onVis = () => {
    if (document.hidden) {
      clear();
      touchX = 0;
      touchTuck = false;
      pointerDown = false;
      canvasPointerId = null;
      // A hidden tab keeps rendering audio; that battery belongs to the user.
      suspendAudio();
    } else {
      resumeAudio();
    }
  };

  const onPointer = (e: PointerEvent) => {
    resumeAudio();
    if (e.pointerType === "mouse") return;
    if (e.button !== 0 && e.pointerType !== "touch") return;
    const rect = canvas.getBoundingClientRect();
    if (rect.width <= 0) return;
    const x = (e.clientX - rect.left) / rect.width;
    pointerDown = true;
    canvasPointerId = e.pointerId;
    touchX = x < 0.5 ? -1 : 1;
    try {
      canvas.setPointerCapture(e.pointerId);
    } catch {}
    e.preventDefault();
  };
  const onPointerMove = (e: PointerEvent) => {
    if (!pointerDown || canvasPointerId !== e.pointerId) return;
    const rect = canvas.getBoundingClientRect();
    if (rect.width <= 0) return;
    const x = (e.clientX - rect.left) / rect.width;
    touchX = x < 0.5 ? -1 : 1;
  };
  const onPointerUp = (e: PointerEvent) => {
    if (canvasPointerId !== null && e.pointerId !== canvasPointerId) return;
    pointerDown = false;
    canvasPointerId = null;
    touchX = 0;
    try {
      canvas.releasePointerCapture(e.pointerId);
    } catch {}
  };
  const onMenu = (e: Event) => e.preventDefault();

  window.addEventListener("keydown", onDown);
  window.addEventListener("keyup", onUp);
  window.addEventListener("blur", clear);
  document.addEventListener("visibilitychange", onVis);
  canvas.addEventListener("pointerdown", onPointer);
  canvas.addEventListener("pointermove", onPointerMove);
  canvas.addEventListener("pointerup", onPointerUp);
  canvas.addEventListener("pointercancel", onPointerUp);
  canvas.addEventListener("contextmenu", onMenu);

  return () => {
    window.removeEventListener("keydown", onDown);
    window.removeEventListener("keyup", onUp);
    window.removeEventListener("blur", clear);
    document.removeEventListener("visibilitychange", onVis);
    canvas.removeEventListener("pointerdown", onPointer);
    canvas.removeEventListener("pointermove", onPointerMove);
    canvas.removeEventListener("pointerup", onPointerUp);
    canvas.removeEventListener("pointercancel", onPointerUp);
    canvas.removeEventListener("contextmenu", onMenu);
  };
}

export function setTouchSteer(v: number): void {
  touchX = v;
  if (v === 0) {
    pointerDown = false;
    canvasPointerId = null;
  }
}

export function setTouchTuck(v: boolean): void {
  touchTuck = v;
}

export function resetTransientInput(): void {
  touchX = 0;
  touchTuck = false;
  pointerDown = false;
  canvasPointerId = null;
  steerOverride = null;
  tuckOverride = null;
}

export function setInjectedKeys(codes: string[]): void {
  injected = new Set(codes);
}

export function clearInjectedKeys(): void {
  injected = null;
}

export function setSteerOverride(v: number | null): void {
  steerOverride = v;
}

export function setTuckOverride(v: boolean | null): void {
  tuckOverride = v;
}

function gamepadX(): number {
  const pads = typeof navigator !== "undefined" ? navigator.getGamepads?.() : null;
  if (!pads) return 0;
  for (const pad of pads) {
    if (!pad) continue;
    let x = 0;
    const ax = pad.axes[0] ?? 0;
    const mag = Math.abs(ax);
    if (mag >= 0.18) x += ((mag - 0.18) / 0.82) * Math.sign(ax);
    if (pad.buttons[14]?.pressed) x -= 1;
    if (pad.buttons[15]?.pressed) x += 1;
    if (x !== 0) return Math.max(-1, Math.min(1, x));
  }
  return 0;
}

function gamepadTuck(): boolean {
  const pads = typeof navigator !== "undefined" ? navigator.getGamepads?.() : null;
  if (!pads) return false;
  for (const pad of pads) {
    // The face button or either trigger; the d-pad stays steering's.
    if (pad?.buttons[0]?.pressed || pad?.buttons[6]?.pressed || pad?.buttons[7]?.pressed) {
      return true;
    }
  }
  return false;
}

export function readMoveX(): number {
  if (steerOverride !== null) {
    return Math.max(-1, Math.min(1, -steerOverride));
  }
  const keys = activeKeys();
  let x = 0;
  if (keys.has("KeyA") || keys.has("ArrowLeft")) x -= 1;
  if (keys.has("KeyD") || keys.has("ArrowRight")) x += 1;
  x += touchX;
  x += gamepadX();
  return Math.max(-1, Math.min(1, x));
}

export function readTuck(): boolean {
  if (tuckOverride !== null) return tuckOverride;
  const keys = activeKeys();
  return TUCK_KEYS.some((code) => keys.has(code)) || touchTuck || gamepadTuck();
}

export function justHeld(code: string): boolean {
  return activeKeys().has(code);
}

export function getSteerForProbe(): number {
  if (steerOverride !== null) return steerOverride;
  return -readMoveX();
}
